import { useEffect, useState } from "react";
import { AlertTriangle, MailWarning, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const PaymentFailures = ({ tAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payment-failures");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const sendReminder = async (item) => {
    if (!window.confirm(tAdmin.failures.confirmRemind.replace("{name}", item.donor_name))) return;
    setSending(item.id);
    try {
      await api.post(`/admin/payment-failures/${item.id}/remind`);
      toast.success(tAdmin.failures.sent);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  return (
    <div data-testid="payment-failures-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#64748B] flex items-start gap-2 max-w-3xl">
          <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
          {tAdmin.failures.intro}
        </p>
        <button
          onClick={fetchAll}
          className="text-[#64748B] hover:text-[#002B49] inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md hover:bg-black/5"
          data-testid="failures-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]">...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center" data-testid="no-failures">
          <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-3" />
          <p className="text-[#64748B]">{tAdmin.failures.empty}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FDFBF7] border-b border-black/5">
                <tr className="text-left text-[#64748B] uppercase text-[11px] tracking-wider">
                  <th className="px-5 py-3 font-medium">{tAdmin.failures.donor}</th>
                  <th className="px-5 py-3 font-medium">{tAdmin.failures.amount}</th>
                  <th className="px-5 py-3 font-medium">{tAdmin.failures.failed}</th>
                  <th className="px-5 py-3 font-medium">{tAdmin.failures.lastReminder}</th>
                  <th className="px-5 py-3 font-medium text-right">{tAdmin.failures.actions}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr
                    key={it.id}
                    className="border-b border-black/5 last:border-0 hover:bg-[#FDFBF7]"
                    data-testid={`failure-row-${i}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#0A192F]">{it.donor_name}</div>
                      <div className="text-xs text-[#64748B]">{it.donor_email}</div>
                    </td>
                    <td className="px-5 py-4 font-serif text-lg font-semibold text-[#002B49] whitespace-nowrap">
                      {Number(it.amount).toFixed(2)} {it.currency}
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 text-[11px] font-semibold">
                        <AlertTriangle size={11} /> {it.failed_payments_count}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-[#64748B] whitespace-nowrap">
                      {it.reminder_sent_at ? (
                        <span>
                          {fmtDate(it.reminder_sent_at)}{" "}
                          <span className="text-xs">({it.reminders_sent_count || 1})</span>
                        </span>
                      ) : (
                        <span className="text-[#C5A059]">{tAdmin.failures.never}</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => sendReminder(it)}
                        disabled={sending === it.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[#C5A059] hover:bg-[#B38F48] text-white disabled:opacity-50"
                        data-testid={`failure-remind-${i}`}
                      >
                        <MailWarning size={12} />
                        {sending === it.id ? "..." : tAdmin.failures.remind}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentFailures;
